import { zodResolver } from "@hookform/resolvers/zod";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
    InputAdornment
} from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import { OfferReadOnlyButton } from "../../components/buttons/OfferReadOnlyButton";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";

import { useDialogBetter } from "../../hooks/useDialogBetter";
import {
    useContactSubjects,
    useContactsForPerson,
    useCreateContactMutation,
    useEditContactMutation,
} from "../../services/contactsService";
import { useCustomerByPersonId } from "../../services/customersService";
import { useGetPersonDetails } from "../../services/personRepositoryService";
import { ProductSelector } from "../LeadsPage/ProductSelector";
import { CreateTaskDialog } from "../TaskPage/CreateTaskDialog";
import { EditConnectedPersonDialog } from "./EditConnectedPersonDialog";
import { formatPhoneNumer } from "../../helpers/methods";
import LeadsContactsOffersAgrementsTabs from "./LeadsContactsOffersAgrementsTabs";
import { AdditionalPhonesButton } from "../../components/buttons/AdditionalPhonesButton";
import { AdditionalEmailsButton } from "../../components/buttons/AdditionalEmailsButton";
import AgreementRawPayments from "./AgreementRawPayments";
import AgreementPaymentSchedule from "./AgreementPaymentSchedule";
import { isEmptyArray } from "../../helpers/methods";
import DateService from "../../services/dateService";
import { roundToDecimalPlaces } from "../../helpers/methods";
import { formatCurrency } from "../../components/common/PriceDisplay";

export const editConnectedPersonFormSchema = z.object({
    name: z.string().optional(),
    surname: z.string().optional(),
    role: z.string().optional(),
    connectedCompany: z.string().optional(),
    companyFunction: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),

    meansOfContact: z
        .string()
        .min(1, { message: t("contacts.validation.meansOfContactRequired") })
        .default(""),
    subject: z
        .string()
        .min(1, { message: t("contacts.validation.subjectRequired") })
        .default(""),
    contactDate: z
        .string()
        .min(1, { message: t("contacts.validation.contactDateRequired") })
        .default(""),
    contactNote: z
        .string()
        .min(1, { message: t("contacts.validation.contactNoteRequired") })
        .default(""),
    productIds: z.array(z.string()).default([]),
    rescheduled_payments: z.array().optional(),
    agreement_overdue_payment_id: z.string().optional()
});


export function ContactsDialog(props) {
    const { isPreview = false, isEdit = false, agreementOverduePaymentId = null, overduePaymentBalance = null } = props;
    const today = new Date();
    const maxDate = today.toISOString().split("T")[0];

    const [paymentsLocal, setPaymentsLocal] = useState(props.rowData?.currentPayments || [])

    const validatePayments = () => {
        let allPaymentsHasValidDueDate = true
        const paymentsTemp = [...paymentsLocal]
        for ( let i = 0; i < paymentsTemp.length; i++) {
            let payment = paymentsTemp[i]
            if (payment.is_paid) {
                continue
            }
            if (!payment.due_date || (payment.due_date < DateService.getIsoDateFromDatetime(today))) {
                payment.invalid_due_date = true
                allPaymentsHasValidDueDate = false
            } else {
                payment.invalid_due_date = false
            }
        }

        let allPaymentsHasValidPaymentAmount = true
        for ( let i = 0; i < paymentsTemp.length; i++) {
            let payment = paymentsTemp[i]
            if (payment.is_paid) {
                continue
            }
            const isValidPaymentSplit = (
                roundToDecimalPlaces(props.rowData?.rawPayments?.find(rawPayment=>rawPayment.id === payment.agreement_payment_parent_id)?.agreement_payment_amount, 2) === 
                paymentsTemp.filter(currPayment=>currPayment.agreement_payment_parent_id === payment.agreement_payment_parent_id).reduce((currValue, currPayment)=>roundToDecimalPlaces(currValue + parseFloat(currPayment.agreement_payment_amount),2), 0)
            )
            if (payment.agreement_payment_amount <= 0 || !isValidPaymentSplit) {
                payment.invalid_agreement_payment_amount = true
                allPaymentsHasValidPaymentAmount = false
            } else {
                payment.invalid_agreement_payment_amount = false
            }
        }

        setPaymentsLocal(paymentsTemp)
        return allPaymentsHasValidDueDate && allPaymentsHasValidPaymentAmount

    }

    const { handleSubmit, control, formState, watch, setValue, getValues } = useForm({
        resolver: zodResolver(editConnectedPersonFormSchema),
        defaultValues: {
            contactDate: maxDate,
            productIds: [],
        },
    });

    const btnEnabled = watch(["subject", "meansOfContact", "contactDate", "contactNote"]).every(
        (v) => Boolean(v),
    );

    const { data: personDetails, isLoading: isLoadingPerson } = useGetPersonDetails(
        props.personId,
        {
            enabled: !!props.personId,
        },
    );

    const { data: contactsData, isLoading: isLoadingContacts } = useContactsForPerson(
        props.personId,
        {
            enabled: isPreview && !!props.personId,
        },
    );

    const { data: customer } = useCustomerByPersonId(props.personId);

    useEffect(() => {
        if (props.rowData) {
            const { way, subject, date, note, products, productsIds } = props.rowData;
            if (way) {
                setValue("meansOfContact", way);
            }
            if (subject) {
                setValue("subject", subject);
            }
            if (date) {
                setValue("contactDate", date);
            }
            if (note) {
                setValue("contactNote", note);
            }
            if (productsIds && productsIds.length > 0) {
                setValue("productIds", productsIds);
            }
            if (products && products.length > 0) {
                setValue(
                    "productIds",
                    products.map((p) => p.id),
                );
            }
        }
    }, [props.rowData, setValue]);

    const { openDialog: openEditConnectedPersonDialog, closeDialog } = useDialogBetter();
    const { openDialog: openNestedPassport, closeDialog: closeNestedPassport } = useDialogBetter();
    const { openDialog: openCreateTaskDialog } = useDialogBetter();

    const { mutate: createContact } = useCreateContactMutation();
    const { mutate: editContact } = useEditContactMutation()
    const { data: contactSubjects } = useContactSubjects({
        select: (data) =>
            data.map((subject) => ({
                label: t(`contacts.subjects.${subject.contsubj_name_eng}`),
                value: subject.contsubj_name_eng,
            })),
    });

    const onSubmit = (data) => {
        if (isPreview) {
            return;
        }
        
        if (!validatePayments()) {
            return
        }

        if (isEdit) {
            editContact({
                id: props.rowData.id,
                date: data.contactDate,
                note: data.contactNote,
                subject: data.subject,
                way: data.meansOfContact,
                product_ids: data.productIds,
            })
        } else {
            createContact({
                date: data.contactDate,
                note: data.contactNote,
                person_id: props.personId,
                subject: data.subject,
                way: data.meansOfContact,
                product_ids: data.productIds,
                agreement_overdue_payment_id: props.agreementOverduePaymentId,
                rescheduled_payments: !isEmptyArray(paymentsLocal) ? paymentsLocal : [],
            });
            // submit();
        }

        if (props.onSubmitCallback) {
            props.onSubmitCallback();
        }
        props.onClose();
    };

    const onError = (errors) => {
        console.log(errors);
        Object.values(errors).forEach((error) => {
            toast.custom(
                () => (
                    <SnackbarError message={error.message || t("contacts.errors.generalError")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        });
    };

    // Use related persons from the API
    const connectedPersons = personDetails?.related_persons || [];

    const getFormProductIds = () => getValues("productIds");
    const setFormProductIds = (ids) => setValue("productIds", ids);

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={isEdit ? t("contacts.dialog.editTitle") : isPreview ? t("contacts.dialog.previewTitle") : t("contacts.dialog.title")}
            maxWidth={"lg"}
            showDialogActions
            titleSx={{
                pb: 0,
            }}
            headerComponent={
                <Typography
                    variant={"subtitle1"}
                    sx={{ color: "text.secondary", mx: "auto", mb: 1 }}
                >
                    {isPreview
                        ? t("contacts.dialog.previewSubtitle") :
                    isEdit ?
                        ""
                        : t("contacts.dialog.subtitle")}
                </Typography>
            }
        >
            <Grid container direction={"column"} sx={{ mt: 2, position: "relative" }}>
                <Grid container direction={"row"}>
                    <Grid container direction={"column"} xs={6} spacing={2} sx={{ pr: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                            {t("contacts.dialog.personalData")}
                        </Typography>
                        {isLoadingPerson ? (
                            <Grid
                                item
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                sx={{ height: "100%" }}
                            >
                                <CircularProgress size={24} />
                            </Grid>
                        ) : (
                            <>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={false}
                                        label={t("contacts.dialog.firstName")}
                                        value={personDetails?.name || ""}
                                        sx={{
                                        "-webkit-text-fill-color": "black",
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { 
                                            "fontColor": "black",
                                            "fontWeight": "500"}
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={false}
                                        label={t("contacts.dialog.lastName")}
                                        value={personDetails?.surname || ""}
                                        sx={{
                                        "-webkit-text-fill-color": "black",
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { 
                                            "fontColor": "black",
                                            "fontWeight": "500"}
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={false}
                                        label={t("contacts.dialog.primary_email")}
                                        value={personDetails?.email?.email_address || ""}
                                        sx={{
                                        "-webkit-text-fill-color": "black",
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <AdditionalEmailsButton personId={props.personId} readOnly={false}/>
                                                </InputAdornment>
                                            ),
                                            readOnly: true,
                                            style: { 
                                            "fontColor": "black",
                                            "fontWeight": "500"}
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={false}
                                        label={t("contacts.dialog.primary_phone")}
                                        value={formatPhoneNumer(personDetails?.phone?.phone_number || "")}
                                        sx={{
                                        "-webkit-text-fill-color": "black",
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <AdditionalPhonesButton personId={props.personId} readOnly={false}/>
                                                </InputAdornment>
                                            ),
                                            readOnly: true,
                                            style: { 
                                            "fontColor": "black",
                                            "fontWeight": "500"}
                                        }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Divider orientation="vertical" flexItem />
                    <Grid container direction={"column"} xs={6} spacing={2} sx={{ pl: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                            {t("contacts.dialog.address")}
                        </Typography>
                        {isLoadingPerson ? (
                            <Grid
                                item
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                sx={{ height: "100%" }}
                            >
                                <CircularProgress size={24} />
                            </Grid>
                        ) : (
                            <>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={true}
                                        label={t("contacts.dialog.residentialAddress")}
                                        value={
                                            personDetails?.living_address
                                                ? `${personDetails.living_address.street} ${personDetails.living_address.building}${
                                                      personDetails.living_address.flat_nr
                                                          ? `/${personDetails.living_address.flat_nr}`
                                                          : ""
                                                  }, ${personDetails.living_address.city}`
                                                : t("contacts.dialog.noData")
                                        }
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={true}
                                        label={t("contacts.dialog.correspondenceAddress")}
                                        value={
                                            personDetails?.mailing_address
                                                ? `${personDetails.mailing_address.street} ${personDetails.mailing_address.building}${
                                                      personDetails.mailing_address.flat_nr
                                                          ? `/${personDetails.mailing_address.flat_nr}`
                                                          : ""
                                                  }, ${personDetails.mailing_address.city}`
                                                : t("contacts.dialog.noData")
                                        }
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Grid>
                                                    <Grid item >
                        <Typography variant="body1" sx={{ fontWeight: "medium", mb: 2 }}>
                            {t("contacts.dialog.relatedPersons")}
                        </Typography>
                        {isLoadingPerson ? (
                            <Box display="flex" justifyContent="center" p={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : connectedPersons && connectedPersons.length > 0 ? (
                            <Autocomplete
                                sx={{
                                    "& .MuiAutocomplete-popupIndicatorOpen": {
                                        transform: "none",
                                    },
                                }}
                                size="small"
                                options={personDetails?.related_persons || []}
                                autoHighlight
                                popupIcon={<SearchIcon />}
                                getOptionLabel={(option) => `${option.name} ${option.surname}`}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <Grid
                                            container
                                            justifyContent={"space-between"}
                                            alignItems={"center"}
                                            sx={{ px: 2 }}
                                        >
                                            <Grid item>
                                                {option.name} {option.surname}
                                            </Grid>
                                            <Grid item>
                                                {!option.customer_id && option.person_id && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => {
                                                            openEditConnectedPersonDialog({
                                                                Component:
                                                                    EditConnectedPersonDialog,
                                                                componentProps: {
                                                                    personId: option.person_id,
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <InfoIcon />
                                                    </IconButton>
                                                )}
                                                {!!option.customer_id && (
                                                    <ClientPassportButton
                                                        clientId={option.customer_id}
                                                        clientType="customer"
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("contacts.dialog.searchRelatedPersons")}
                                    />
                                )}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                {t("contacts.dialog.noRelatedPersons")}
                            </Typography>
                        )}
                    </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
                <Divider orientation="horizontal" flexItem sx={{ mt: 3 }} />
                
                {/* Cr hide clauses */}
                {/* Consents and Related Persons Row */}
                {/* <Grid container direction={"row"} spacing={3} sx={{ mt: 2 }}>
                    
                    <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: "medium", mb: 2 }}>
                            {t("contacts.dialog.consents")}
                        </Typography>
                        <Grid container direction={"column"} alignItems={"start"}>
                            <ClausesForm />
                        </Grid>
                    </Grid>
                    <Grid item >
                        <Typography variant="body1" sx={{ fontWeight: "medium", mb: 2 }}>
                            {t("contacts.dialog.relatedPersons")}
                        </Typography>
                        {isLoadingPerson ? (
                            <Box display="flex" justifyContent="center" p={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : connectedPersons && connectedPersons.length > 0 ? (
                            <Autocomplete
                                sx={{
                                    "& .MuiAutocomplete-popupIndicatorOpen": {
                                        transform: "none",
                                    },
                                }}
                                size="small"
                                options={personDetails?.related_persons || []}
                                autoHighlight
                                popupIcon={<SearchIcon />}
                                getOptionLabel={(option) => `${option.name} ${option.surname}`}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <Grid
                                            container
                                            justifyContent={"space-between"}
                                            alignItems={"center"}
                                            sx={{ px: 2 }}
                                        >
                                            <Grid item>
                                                {option.name} {option.surname}
                                            </Grid>
                                            <Grid item>
                                                {!option.customer_id && option.person_id && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => {
                                                            openEditConnectedPersonDialog({
                                                                Component:
                                                                    EditConnectedPersonDialog,
                                                                componentProps: {
                                                                    personId: option.person_id,
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <InfoIcon />
                                                    </IconButton>
                                                )}
                                                {!!option.customer_id && (
                                                    <ClientPassportButton
                                                        clientId={option.customer_id}
                                                        clientType="customer"
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("contacts.dialog.searchRelatedPersons")}
                                    />
                                )}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                {t("contacts.dialog.noRelatedPersons")}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Divider orientation="horizontal" flexItem sx={{ mt: 3 }} /> */}
                
                <Grid container sx={{ mt: 2 }} direction={"column"}>
                    {/* @ts-ignore */}
                    < LeadsContactsOffersAgrementsTabs personId={props.personId} customerId={customer?.id}/>
                </Grid>
                <Divider orientation="horizontal" flexItem sx={{ mt: 2 }} />
                <Grid container sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "medium", mb: 2 }}>
                        {t("contacts.dialog.contactParameters")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs>
                            <ControlledSelect
                                control={control}
                                name="meansOfContact"
                                label={`${t("contacts.dialog.contactMethod")}*`}
                                data={[
                                    {
                                        label: t("contacts.dialog.contactMethods.phone"),
                                        value: "Phone",
                                    },
                                    {
                                        label: t("contacts.dialog.contactMethods.email"),
                                        value: "Email",
                                    },
                                    {
                                        label: t("contacts.dialog.contactMethods.meeting"),
                                        value: "Meeting",
                                    },
                                    {
                                        label: t("contacts.dialog.contactMethods.other"),
                                        value: "Other",
                                    },
                                ]}
                                disabled={isPreview}
                            />
                        </Grid>
                        <Grid item xs>
                            <ControlledSelect
                                control={control}
                                name="subject"
                                label={`${t("contacts.dialog.subject")}*`}
                                data={contactSubjects || []}
                                disabled={isPreview}
                            />
                        </Grid>
                        <Grid item xs>
                            <ControlledTextField
                                control={control}
                                name="contactDate"
                                label={`${t("contacts.dialog.contactDate")}*`}
                                props={{
                                    type: "date",
                                    disabled: isPreview,
                                    InputLabelProps: {
                                        shrink: true,
                                    },
                                    InputProps: {
                                        inputProps: {
                                            max: maxDate,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs sx={{ mt: 2 }}>
                        <ControlledTextField
                            control={control}
                            name="contactNote"
                            label={`${t("contacts.dialog.contactNote")}*`}
                            props={{
                                disabled: isPreview,
                                InputProps: {
                                    rows: 3,
                                    multiline: true,
                                    inputComponent: "textarea",
                                },
                            }}
                        />
                    </Grid>
                    {agreementOverduePaymentId && isPreview && <Grid item xs={12} sx={{mt:3}}>
                        <AgreementRawPayments payments={props.rowData?.rawPayments}/>
                    </Grid>}
                    {agreementOverduePaymentId && !isPreview && <Grid item xs={12} sx={{mt:2}}>
                        <AgreementPaymentSchedule rawPayments={props.rowData?.rawPayments} paymentsLocal={paymentsLocal} setPaymentsLocal={setPaymentsLocal}/>
                    </Grid>}
                    {agreementOverduePaymentId && !isPreview &&  <Grid item xs={12} alignSelf={"flex-end"}>
                        <Typography variant="body1" fullWidth color={"error"} sx={{ fontWeight: "medium", mt: 2, textAlign: "right" }}>
                            {`${t("table.agreements.agreementOverduePaymentBalance")}: ${formatCurrency(overduePaymentBalance || 0)}`}
                        </Typography>
                    </Grid>}
                    
                    {/* Product selection section */}
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "medium", mb: 2 }}>
                            {t("contacts.dialog.relatedProducts")}
                        </Typography>
                        <ProductSelector
                            getFormProductIds={getFormProductIds}
                            setFormProductIds={setFormProductIds}
                            disabled={isPreview}
                            previewProducts={isPreview ? props.rowData?.products : undefined}
                        />
                    </Grid>
                </Grid>

                <Grid container sx={{ mt: 4, mb: 2 }} alignItems="center">
                    <Grid item xs={4} display="flex" justifyContent="flex-start">
                        {isPreview && <OfferReadOnlyButton offerId={props.rowData?.offerId} />}
                    </Grid>

                    <Grid item xs={4} display="flex" justifyContent="center">
                        {!isPreview && (
                            <Button
                                variant="contained"
                                onClick={handleSubmit(onSubmit, onError)}
                                disabled={!btnEnabled}
                                sx={{ width: "200px" }}
                            >
                                {isEdit ? t("contacts.dialog.edit") : t("contacts.dialog.add")}
                            </Button>
                        )}
                    </Grid>
                    <Grid item xs={4} display="flex" justifyContent="flex-end">
                        {!isPreview && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                    openCreateTaskDialog({
                                        Component: CreateTaskDialog,
                                        componentProps: {
                                            customerId: customer?.id,
                                            defaultSellerId: props.defaultSellerId,
                                        },
                                    })
                                }
                            >
                                {t("contacts.dialog.newTask")}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
